import { ReactNode } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";
import { Editions, ProductKeys } from "@atlassiansox/asc-stargate";

import { Adg4Button } from "@/components/adg4/button/src";
import { useCrossFlowFrontendTouchpoint } from "@/components/cross-flow/touchpoint";
import { useLocalizedHref } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";

const CtaSection = () => {
    const signupLink = useLocalizedHref(
        "/try/cloud/signup?bundle=jira-product-discovery"
    );
    const { openCrossFlow } = useCrossFlowFrontendTouchpoint({
        productKey: ProductKeys.JIRA_PRODUCT_DISCOVERY,
        edition: Editions.FREE,
        redirectUrl: signupLink
    });

    return (
        <section css={container}>
            <Row layout="12">
                <div css={sectionStyle}>
                    <h2 css={[textStyle, accent]}>
                        <FormattedMessage
                            id="JiraProductDiscoveryPage.cta.section.heading"
                            defaultMessage="<span>Build</span> better features, not more features."
                            values={{
                                span: (chunks: ReactNode) => (
                                    <span>{chunks}</span>
                                )
                            }}
                        />
                    </h2>
                    <p css={textStyle} className="body-lg">
                        <FormattedMessage
                            id="JiraProductDiscoveryPage.cta.section.subheading"
                            defaultMessage="Leave the guess work behind and build products your customers love."
                        />
                    </p>
                    <Adg4Button
                        color="yellow"
                        variant="primary"
                        size="small"
                        dataEventProps={{
                            "data-event-component": "linkButton",
                            "data-event-container": "ctaSection",
                            "data-event": "clicked",
                            "data-product-key": "jiraProductDiscovery",
                            "data-label-english": "Get it free",
                            "data-track-event":
                                "jiraProductDiscovery.get-it-free-button-clicked"
                        }}
                        href={signupLink}
                        onClick={openCrossFlow}
                        css={button}
                    >
                        <FormattedMessage
                            id="JiraProductDiscoveryPage.cta.section.cta.text"
                            defaultMessage="Get it free"
                        />
                    </Adg4Button>
                </div>
            </Row>
        </section>
    );
};

export default CtaSection;

const container = css({
    overflowX: "hidden",
    maxWidth: "100%"
});
const sectionStyle = css({
    "position": "relative",
    "textAlign": "center",
    "marginBlockEnd": token("space.800"),
    "paddingBlockStart": token("space.800"),
    "paddingBlockEnd": token("space.400"),
    "display": "flex",
    "flexDirection": "column",
    "alignItems": "center",
    "gap": token("space.200"),
    "::before": {
        position: "absolute",
        zIndex: -1,
        content: "",
        insetInline: token("space.negative.400"),
        insetBlock: token("space.negative.300"),
        backgroundColor: token("color.background.accent.lime.subtle"),
        transform: "translateX(1.5rem) translateY(.5rem)",
        [media.only.sm]: {
            transform: "translateY(.5rem)",
            clipPath: "polygon(5% 0%, 95% 0%, 95% 100%, 5% 100%)"
        },
        [media.above.md]: {
            transform: "translateX(1.5rem) translateY(1rem)",
            clipPath: "polygon(7.5% 15%, 100% 15%, 98.5% 100%, 7.25% 98%)"
        }
    },
    "::after": {
        position: "absolute",
        zIndex: -1,
        content: "",
        insetInline: token("space.negative.400"),
        insetBlock: token("space.negative.200"),
        backgroundColor: token("color.background.brand.bold"),
        backgroundImage:
            "url(https://dam-cdn.atl.orangelogic.com/AssetLink/4xev2v5qo2qdi732b226j0q843xv4048.svg)",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "calc(100% - 16px) calc(100% - 16px)",
        backgroundSize: "98px 100px",
        clipPath: "polygon(0% 0%, 100% 0%, 98% 98%, 0% 100%)",
        [media.above.sm]: {
            clipPath: "polygon(0% 0%, 100% 0%, 98% 100%, 2% 100%)",
            backgroundPosition: `90% ${token("space.800")}`,
            backgroundSize: "120px 123px"
        }
    }
});
const textStyle = css({
    color: `${token("color.text.inverse")} !important`,
    marginBlock: 0,
    [media.above.sm]: {
        width: "70%",
        marginInline: "auto"
    }
});
const accent = css({
    "::before": {
        position: "absolute",
        width: "38px",
        height: "38px",
        content: "",
        background:
            "url(https://dam-cdn.atl.orangelogic.com/AssetLink/06128ya2s57wobmg652444h75uc081a3.svg) no-repeat top left",
        backgroundSize: "38px 38px",
        display: "inline-block",
        transform: "translate(-50%, -40%)"
    }
});
const button = css({
    marginBlockStart: token("space.200")
});
